// src/components/ui/2-organisms/VideoModal/VideoModal.js

import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../../1-molecules/Icon/Icon';
import styles from './VideoModal.module.css';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';

/**
 * Full-screen video modal component
 * @param {string} videoUrl - URL of the video to play
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} [title] - Optional video title
 */
const VideoModal = ({ videoUrl, isOpen, onClose, title = 'Video' }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasAudio, setHasAudio] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      // Auto-play when modal opens
      videoRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
        setIsPlaying(false);
      });
      setIsPlaying(true);
    } else if (!isOpen && videoRef.current) {
      // Pause when modal closes
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isOpen]);

  useBodyScrollLock(isOpen);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      
      // Check if video has audio track
      const videoElement = videoRef.current;
      const hasAudioTrack = videoElement.mozHasAudio ||
                           Boolean(videoElement.webkitAudioDecodedByteCount) ||
                           Boolean(videoElement.audioTracks && videoElement.audioTracks.length);
      
      // If we can't detect using the above methods, check if the video element has volume
      if (hasAudioTrack === undefined) {
        // Assume it has audio if we can't detect
        setHasAudio(true);
      } else {
        setHasAudio(hasAudioTrack);
      }
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInteraction = () => {
    setShowControls(true);
    
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    // Hide controls after 3 seconds of no interaction
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className={styles.overlay} 
      onClick={handleInteraction}
      onMouseMove={handleInteraction}
      onTouchStart={handleInteraction}
    >
      <div className={styles.modal}>
        {/* Close button */}
        <div className={`${styles.header} ${!showControls ? styles.hidden : ''}`}>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close video"
          >
            <Icon name="close" size="m" />
          </button>
        </div>

        {/* Video player */}
        <div className={styles.videoWrapper} onClick={handlePlayPause}>
          <video
            ref={videoRef}
            src={videoUrl}
            className={styles.video}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            muted={isMuted}
            playsInline
            webkit-playsinline="true"
          />
        </div>

        {/* Controls */}
        <div className={`${styles.controls} ${!showControls ? styles.hidden : ''}`}>
          <button
            className={styles.controlButton}
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <Icon name={isPlaying ? 'pause' : 'play'} size="m" />
          </button>

          <div className={styles.timeDisplay}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          <div className={styles.progressBar} onClick={handleSeek}>
            <div className={styles.progressTrack}>
              <div 
                className={styles.progressFill}
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <div 
                className={styles.progressThumb}
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          </div>

          {hasAudio && (
            <button
              className={styles.controlButton}
              onClick={handleMuteToggle}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              <Icon name={isMuted ? 'volume-off' : 'volume'} size="m" />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VideoModal;
